import React, { useState, useEffect, useRef, forwardRef } from 'react';

const MediaSwitcher = forwardRef(({ files }, externalRef) => {
  const [isVisible, setIsVisible] = useState(false);
  const internalRef = useRef(null);

  const currentFile = files[0]; // Always show the first file

  const isVideo = (file) => {
    if (!file) return false;
    const videoExtensions = ['mp4', 'webm', 'ogg'];
    // Handle both string URLs and objects with preview_file/processed_file
    const fileUrl = typeof file === 'string' ? file : (file?.preview_file || file?.processed_file);
    if (!fileUrl) return false;
    const extension = fileUrl.split('.').pop();
    return videoExtensions.includes(extension);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Trigger when 10% of the element is visible
    );

    const refToUse = externalRef ? externalRef.current : internalRef.current;

    if (refToUse) {
      observer.observe(refToUse);
    }

    return () => {
      if (refToUse) {
        observer.unobserve(refToUse);
      }
    };
  }, [externalRef]);

  // Use external ref if provided, otherwise use internal ref
  const mediaRef = externalRef || internalRef;

  return (
    <div className="relative h-96 w-auto" ref={mediaRef}>
      {!currentFile ? (
        <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
          <span className="text-gray-400 text-sm">Procesando...</span>
        </div>
      ) : isVideo(currentFile) ? (
        isVisible && (
          <video
            className="w-full h-full object-cover"
            muted
            autoPlay
            loop
            playsInline
          >
            <source src={typeof currentFile === 'string' ? currentFile : (currentFile?.preview_file || currentFile?.processed_file)} type={`video/${(typeof currentFile === 'string' ? currentFile : (currentFile?.preview_file || currentFile?.processed_file)).split('.').pop()}`} />
            Your browser does not support the video tag.
          </video>
        )
      ) : (
        <div
          className={`bg-cover bg-center h-full w-full transition-opacity duration-500 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ backgroundImage: `url(${typeof currentFile === 'string' ? currentFile : (currentFile?.preview_file || currentFile?.processed_file)})` }}
        >
          {!isVisible && (
            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500">Loading...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

MediaSwitcher.displayName = 'MediaSwitcher';

export default MediaSwitcher;
